import { useEffect, useState } from 'react';
import { FilePlus2 } from 'lucide-react';
import {
  Button,
  Input,
  Modal,
  Select,
  Textarea,
  useToast,
  type SelectOption,
} from '../../ui';
import type { AddTaskDocumentInput } from '../../features/sprintboard/useTaskDetail';
import type { TeamDocument } from '../../features/documents/types';

export interface AddTaskDocumentModalProps {
  open: boolean;
  onClose: () => void;
  taskTitle: string;
  onAdd: (input: AddTaskDocumentInput) => Promise<TeamDocument>;
}

/** The compact type choices for a task-attached document (§18). */
const DOC_TYPE_OPTIONS: SelectOption[] = [
  { value: 'Design Doc', label: 'Design Doc' },
  { value: 'PRFAQ', label: 'PRFAQ' },
  { value: 'Runbook', label: 'Runbook' },
  { value: 'Other', label: 'Other' },
];

/**
 * "+ Add Document" (§18) — a compact form (Title, Type, Link, Short Context)
 * that registers the doc in the SHARED Document Bank tagged with this task (and
 * its goal), so it also appears in the global Documents view — one shared entry,
 * no duplicate.
 */
export function AddTaskDocumentModal({
  open,
  onClose,
  taskTitle,
  onAdd,
}: AddTaskDocumentModalProps) {
  const { toast } = useToast();
  const [title, setTitle] = useState('');
  const [docType, setDocType] = useState('Design Doc');
  const [documentLink, setDocumentLink] = useState('');
  const [context, setContext] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setDocType('Design Doc');
    setDocumentLink('');
    setContext('');
  }, [open]);

  const valid = title.trim() !== '' && documentLink.trim() !== '';

  const submit = async () => {
    if (!valid) {
      toast({
        tone: 'error',
        title: 'Title and link are required',
        description: 'The Document Bank indexes links — a title and link are needed.',
      });
      return;
    }
    setSaving(true);
    try {
      await onAdd({ title, docType, documentLink, context });
      toast({ tone: 'success', title: 'Document added', description: title });
      onClose();
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Couldn’t add document',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={saving ? () => undefined : onClose}
      size="md"
      title={
        <span className="flex items-center gap-2">
          <FilePlus2 className="h-4 w-4 text-accent" aria-hidden="true" />
          Add document ·{' '}
          <span className="font-normal text-zinc-400">{taskTitle}</span>
        </span>
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} onClick={submit} disabled={!valid}>
            Add document
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Title"
          value={title}
          placeholder="e.g. Checkout migration — design"
          onChange={(e) => setTitle(e.target.value)}
        />
        <Select
          label="Type"
          value={docType}
          options={DOC_TYPE_OPTIONS}
          onChange={(e) => setDocType(e.target.value)}
        />
        <Input
          label="Link"
          value={documentLink}
          placeholder="https://quip-amazon.com/…"
          onChange={(e) => setDocumentLink(e.target.value)}
        />
        <Textarea
          label="Short context"
          rows={3}
          value={context}
          placeholder="One or two lines on what this doc covers — powers discovery in the Document Bank."
          onChange={(e) => setContext(e.target.value)}
        />
        <p className="text-xs text-zinc-600">
          Added to the shared Document Bank, tagged to this task — it appears here
          and in Documents, not twice.
        </p>
      </div>
    </Modal>
  );
}

export default AddTaskDocumentModal;
